import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, BookOpen, Mail, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

/** Publisher-facing /publisher/support page — mirrors the buyer Support page. */
export default function PublisherSupport() {
  const [busy, setBusy] = React.useState(false);
  const openChat = () => window.dispatchEvent(new CustomEvent("connexis:open-support"));

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const subject = (fd.get("subject") || "").toString().trim();
    const message = (fd.get("message") || "").toString().trim();
    if (!subject || !message) { toast.error("Subject and message are required"); return; }
    setBusy(true);
    try {
      await api.post("/support/me/messages", { body: `[${subject}]\n${message}` });
      toast.success("Sent — we'll reply within an hour during UK business hours");
      e.target.reset();
      setTimeout(openChat, 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed — try the live chat widget bottom-right");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="publisher-support-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Support</h1>
        <p className="text-[13px] text-slate-500 mt-1">Talk to your publisher success team — integrations, payouts, rejections, anything.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-5">
            <span className="inline-grid place-items-center size-10 rounded-md bg-slate-900 text-white"><Mail className="size-5" strokeWidth={1.6} /></span>
            <div className="mt-4 font-medium text-slate-900">Email</div>
            <a href="mailto:ops@connex.is" className="text-xs text-slate-500 mt-0.5 hover:text-[#0055FF]">ops@connex.is</a>
          </CardContent>
        </Card>

        <button onClick={openChat} className="text-left rounded-xl shadow-soft border border-slate-200 bg-white hover:border-[#0055FF]/40 transition-colors group" data-testid="support-livechat-card">
          <div className="p-5">
            <span className="inline-grid place-items-center size-10 rounded-md bg-[#0055FF] text-white group-hover:scale-105 transition-transform"><MessageSquare className="size-5" strokeWidth={1.6} /></span>
            <div className="mt-4 font-medium text-slate-900">Live chat</div>
            <div className="text-xs text-slate-500 mt-0.5">Click to open · 9am – 6pm GMT, weekdays</div>
            <div className="text-[11px] text-[#0055FF] mt-2 font-medium">Start a conversation →</div>
          </div>
        </button>

        <Link to="/docs">
          <Card className="rounded-xl shadow-soft border-slate-200 hover:border-primary/40 transition-colors cursor-pointer" data-testid="support-docs-link">
            <CardContent className="p-5">
              <span className="inline-grid place-items-center size-10 rounded-md bg-slate-900 text-white"><BookOpen className="size-5" strokeWidth={1.6} /></span>
              <div className="mt-4 font-medium text-slate-900">Docs</div>
              <div className="text-xs text-slate-500 mt-0.5">connex.is/docs</div>
            </CardContent>
          </Card>
        </Link>
      </div>

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <span className="inline-grid place-items-center size-10 rounded-md bg-primary/10 text-primary"><LifeBuoy className="size-5" /></span>
            <div>
              <h3 className="font-medium text-slate-900">Send us a detailed brief</h3>
              <p className="text-[12px] text-slate-500">Webhook errors, missing payouts, rejection reasons — include any IDs you have.</p>
            </div>
          </div>
          <form onSubmit={submit} className="mt-5 space-y-4 max-w-2xl">
            <Input name="subject" required placeholder="Subject — e.g. 401s when posting to /v1/leads/ingest" data-testid="support-subject" />
            <Textarea name="message" required rows={5} placeholder="Include API key prefix, sample payload, lead IDs or campaign IDs where relevant." data-testid="support-message" />
            <div className="flex items-center gap-3">
              <Button disabled={busy} type="submit" className="rounded-full" data-testid="support-submit">{busy ? "Sending…" : "Send to Connexis Support"}</Button>
              <button type="button" onClick={openChat} className="text-[12.5px] text-[#0055FF] hover:underline">or chat live →</button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
